/**
 * PEStart Archiv — Stände speichern, laden, mit heutigem Live-Stand vergleichen.
 */

import React, { useMemo, useState } from 'react';
import { useShallow } from 'zustand/react/shallow';
import useAppStore from '../../store/appStore';
import { useCalculationsSelector } from '../../store/selectors/calculations';
import usePEStartStore from '../../store/usePEStartStore';
import { computePEStartDashboard } from './peStartCalcAdapter';
import { computePEStartAufbauPlan } from './peStartPlanungsBridge';
import {
    buildPEStartArchivDeletePatch,
    createPEStartArchivEintrag,
    normalizePEStartArchiv,
    type PEStartArchivEintrag,
} from './peStartArchivModel';

export type PEStartArchivPanelProps = {
    className?: string;
    /** Nach Öffnen: Parent wechselt z. B. zum Wizard zum Weiterbearbeiten */
    onOpenForEdit?: (entry: PEStartArchivEintrag) => void;
    /** Direkt zu Schritt 4 (Businessplan & PDF) wechseln */
    onGoToTextEngine?: () => void;
};

const INPUT_CLASS =
    'w-full rounded-lg border border-pe-border-strong dark:border-[#333] bg-pe-elevated dark:bg-[#1a1a1a] px-3 py-2.5 text-base text-pe-text dark:text-white outline-none focus:border-[#E6C299]/50';

const MONTH_NAMES = [
    'Jan',
    'Feb',
    'Mär',
    'Apr',
    'Mai',
    'Jun',
    'Jul',
    'Aug',
    'Sep',
    'Okt',
    'Nov',
    'Dez',
] as const;

const PEStartArchivPanel: React.FC<PEStartArchivPanelProps> = ({
    className,
    onOpenForEdit,
    onGoToTextEngine,
}) => {
    const calc = useCalculationsSelector() as any;
    const privateCostData = useAppStore((s) => s.clientData?.privateCostData);
    const clientData = useAppStore((s) => s.clientData);
    const settings = useAppStore((s) => s.settings);
    const setClientData = useAppStore((s) => s.setClientData);

    const peState = usePEStartStore(
        useShallow((s) => ({
            foundingType: s.foundingType,
            subsidies: s.subsidies,
            investments: s.investments,
            staffFlags: s.staffFlags,
            loanPlan: s.loanPlan,
            selectedPlanungsrechnerSzenarioId: s.selectedPlanungsrechnerSzenarioId,
            generatedSections: s.generatedSections,
            privateAltersvorsorge: s.privateAltersvorsorge,
            liquiditaetspufferMonat: s.liquiditaetspufferMonat,
            fachlicheQualifikation: s.fachlicheQualifikation,
            berufserfahrung: s.berufserfahrung,
            kaufmaennischeEignung: s.kaufmaennischeEignung,
            executiveSummaryNotes: s.executiveSummaryNotes,
        }))
    );
    const loadPEStartState = usePEStartStore((s) => s.loadPEStartState);
    const activeArchivId = usePEStartStore((s) => s.activeArchivId);
    const setActiveArchivId = usePEStartStore((s) => s.setActiveArchivId);

    const appSlice = useAppStore(
        useShallow((s) => ({
            planungsAnalysePrognose: s.planungsAnalysePrognose,
            planungsAnalyseArchiv: s.planungsAnalyseArchiv,
            planungsAnalyseActiveSzenarioId: s.planungsAnalyseActiveSzenarioId,
            planungsAnalyseBasisDatenTyp: s.planungsAnalyseBasisDatenTyp,
            peProfitDataVersion: s.peProfitDataVersion,
            clientData: s.clientData,
        }))
    );

    const aufbau = useMemo(
        () =>
            computePEStartAufbauPlan(
                appSlice,
                calc,
                clientData,
                settings,
                peState.selectedPlanungsrechnerSzenarioId
            ),
        [appSlice, calc, clientData, settings, peState.selectedPlanungsrechnerSzenarioId]
    );
    const verbesserungDelta = aufbau.available ? aufbau.summary?.umsatzDeltaMonat ?? 0 : 0;

    const liveModel = useMemo(
        () => computePEStartDashboard(peState, calc, privateCostData, verbesserungDelta),
        [peState, calc, privateCostData, verbesserungDelta]
    );

    const archiv = useMemo(
        () => normalizePEStartArchiv(clientData?.peStartArchiv),
        [clientData?.peStartArchiv]
    );

    /** IST-Umsätze aus Gesamtleistung (Bedien + VK brutto), nur Monate mit Eintrag */
    const istMonate = useMemo(() => {
        const rows = Array.isArray(clientData?.gesamtleistungData)
            ? clientData.gesamtleistungData
            : [];
        const mwstRate =
            liveModel.mwstSatz !== undefined && liveModel.mwstSatz !== null && Number.isFinite(Number(liveModel.mwstSatz))
                ? Number(liveModel.mwstSatz)
                : 19;
        const mwstFaktor = 1 + mwstRate / 100;
        return rows
            .map((m: any, index: number) => {
                const bedien = Number(m?.bedienumsatzBrutto) || 0;
                const vk = Number(m?.vkUmsatzBrutto) || 0;
                const brutto = bedien + vk;
                return {
                    index,
                    label: MONTH_NAMES[index] || m?.monat || `M${index + 1}`,
                    brutto,
                    netto: brutto / mwstFaktor,
                    abgeschlossen: !!m?.abgeschlossen,
                    hasData: brutto > 0,
                };
            })
            .filter((m: { hasData: boolean }) => m.hasData);
    }, [clientData?.gesamtleistungData, liveModel.mwstSatz]);

    const istDurchschnittNetto = useMemo(() => {
        if (istMonate.length === 0) return 0;
        const sum = istMonate.reduce((s: number, m: { netto: number }) => s + m.netto, 0);
        return sum / istMonate.length;
    }, [istMonate]);

    const istDurchschnittBrutto = useMemo(() => {
        if (istMonate.length === 0) return 0;
        const sum = istMonate.reduce((s: number, m: { brutto: number }) => s + m.brutto, 0);
        return sum / istMonate.length;
    }, [istMonate]);

    const [titel, setTitel] = useState('');
    const [note, setNote] = useState('');
    const [selectedId, setSelectedId] = useState<string | null>(activeArchivId);
    const [statusMsg, setStatusMsg] = useState<string | null>(null);

    const effectiveSelectedId = selectedId || activeArchivId || archiv[0]?.id || null;

    const selected = useMemo(
        () => archiv.find((e) => e.id === effectiveSelectedId) || null,
        [archiv, effectiveSelectedId]
    );

    const hasKiText = useMemo(() => {
        if ((peState.generatedSections || []).length > 0) return true;
        const fromSelected = selected?.state?.generatedSections;
        if (Array.isArray(fromSelected) && fromSelected.length > 0) return true;
        const rawMap = (clientData as any)?.peStartGeneratedTexts;
        if (selected?.id && rawMap && typeof rawMap === 'object' && Array.isArray(rawMap[selected.id])) {
            return rawMap[selected.id].length > 0;
        }
        return false;
    }, [peState.generatedSections, selected, clientData]);

    const archivPrognoseSchnittBrutto = useMemo(() => {
        const monate = selected?.kennzahlen?.monate || [];
        if (monate.length === 0) return 0;
        return monate.reduce((s, m) => s + (m.umsatzPrognose || 0), 0) / monate.length;
    }, [selected]);

    const formatCurrency = liveModel.formatCurrency;

    const persistArchiv = (next: PEStartArchivEintrag[]) => {
        if (typeof setClientData !== 'function') {
            setStatusMsg('Speichern nicht möglich (kein setClientData).');
            return;
        }
        setClientData((prev: any) => ({
            ...prev,
            peStartArchiv: next,
        }));
    };

    const buildKennzahlen = () => ({
        planumsatzNetto: liveModel.kpis.planumsatzNetto,
        totpunktMonatBrutto: liveModel.kpis.totpunktMonatBrutto,
        unternehmerlohn: liveModel.kpis.unternehmerlohnKompass,
        startFinanzbedarf: liveModel.kpis.startFinanzbedarf,
        kreditbetrag: liveModel.kpis.finanzbedarf.kreditbetrag,
        rateMonat: liveModel.kpis.finanzbedarf.rateMonat,
        laufzeitJahre: liveModel.kpis.finanzbedarf.tilgungLaufzeitJahre,
        zinssatzProzent: liveModel.kpis.finanzbedarf.zinssatzProzent,
        pufferMonate: liveModel.kpis.finanzbedarf.anlaufPufferMonate,
        breakEvenMonat: liveModel.breakEvenMonat,
        foundingType: liveModel.foundingType,
        monate: liveModel.months.map((m) => ({
            month: m.month,
            umsatzZiel: m.umsatzZiel,
            umsatzPrognose: m.umsatzPrognose,
        })),
    });

    const handleSave = () => {
        const entry = createPEStartArchivEintrag(
            titel ||
                `PEStart ${peState.foundingType === 'TAKEOVER' ? 'Übernahme' : 'Neugründung'} ${new Date().toLocaleDateString('de-DE')}`,
            peState,
            buildKennzahlen(),
            note
        );
        const next = [entry, ...archiv];
        persistArchiv(next);
        setSelectedId(entry.id);
        setActiveArchivId(entry.id);
        setTitel('');
        setNote('');
        setStatusMsg(`Gespeichert: „${entry.titel}“`);
    };

    /** Überschreibt den ausgewählten Archiv-Eintrag mit dem aktuellen Arbeitsstand */
    const handleUpdateSelected = () => {
        if (!selected) {
            setStatusMsg('Bitte zuerst einen Archiv-Eintrag auswählen.');
            return;
        }
        const updated: PEStartArchivEintrag = {
            ...selected,
            timestamp: Date.now(),
            titel: titel.trim() || selected.titel,
            ...(note.trim() ? { note: note.trim() } : selected.note ? { note: selected.note } : {}),
            state: JSON.parse(JSON.stringify(peState)),
            kennzahlen: buildKennzahlen(),
        };
        const next = archiv.map((e) => (e.id === selected.id ? updated : e));
        persistArchiv(next);
        setSelectedId(updated.id);
        setActiveArchivId(updated.id);
        setStatusMsg(`„${updated.titel}“ aktualisiert.`);
    };

    const handleOpenForEdit = (entry: PEStartArchivEintrag) => {
        loadPEStartState(entry.state);
        setSelectedId(entry.id);
        setActiveArchivId(entry.id);
        setTitel(entry.titel);
        setNote(entry.note || '');
        setStatusMsg(
            `„${entry.titel}“ geöffnet — weiterbearbeiten im Wizard, danach oben speichern.`
        );
        onOpenForEdit?.(entry);
    };

    const handleDelete = (id: string) => {
        const entry = archiv.find((e) => e.id === id);
        const label = entry?.titel?.trim() || 'diesen Eintrag';
        if (!window.confirm(`„${label}“ wirklich unwiderruflich löschen?`)) return;
        if (typeof setClientData !== 'function') {
            setStatusMsg('Löschen nicht möglich (kein setClientData).');
            return;
        }
        setClientData((prev: any) => ({
            ...prev,
            ...buildPEStartArchivDeletePatch(prev, id),
        }));
        if (selectedId === id) setSelectedId(null);
        if (activeArchivId === id) setActiveArchivId(null);
        setStatusMsg('Eintrag gelöscht.');
    };

    return (
        <section
            className={`min-w-0 overflow-x-hidden rounded-2xl border border-pe-border dark:border-[#2a2a2a] bg-pe-card dark:bg-[#0f0f0f] p-5 text-base ${className ?? ''}`}
        >
            <header className="mb-6 border-b border-pe-border dark:border-[#222] pb-4">
                <p className="mb-1 text-[10px] font-semibold uppercase tracking-[0.2em] text-stone-500 dark:text-slate-500">
                    PEStart · Schritt 5
                </p>
                <h2 className="text-sm font-semibold uppercase tracking-[0.2em] text-pe-accent-text dark:text-[#E6C299]">
                    Plan ↔ IST-Check &amp; Gespeicherte Stände
                </h2>
                <p className="mt-2 text-sm text-stone-500 dark:text-slate-500">
                    Vergleiche deinen eingefrorenen Plan mit echten Monatsumsätzen aus der Gesamtleistung und verwalte gespeicherte Stände.
                </p>
            </header>

            {/* KI-Text Status & Erinnerung */}
            {!hasKiText ? (
                <div className="mb-6 flex flex-wrap items-center justify-between gap-3 rounded-xl border border-amber-500/40 bg-amber-500/10 p-4">
                    <div>
                        <p className="text-[10px] font-bold uppercase tracking-wider text-amber-500 dark:text-amber-400">
                            Hinweis · Noch kein KI-Text generiert
                        </p>
                        <p className="mt-1 text-sm font-semibold text-pe-text dark:text-white">
                            Für diesen Businessplan wurde noch kein KI-Businessplan-Text erstellt.
                        </p>
                        <p className="mt-0.5 text-xs text-stone-600 dark:text-slate-300">
                            Erinnerung: Du kannst den vollständigen Businessplan-Text (inkl. PDF-Export) in <strong>Schritt 4 · Businessplan &amp; PDF</strong> generieren lassen.
                        </p>
                    </div>
                    {onGoToTextEngine && (
                        <button
                            type="button"
                            onClick={onGoToTextEngine}
                            className="shrink-0 rounded-lg border border-[#E6C299]/50 bg-[#E6C299] px-4 py-2 text-xs font-black uppercase tracking-wider text-black hover:bg-[#f0d4b0]"
                        >
                            Jetzt in Schritt 4 erstellen →
                        </button>
                    )}
                </div>
            ) : (
                <div className="mb-6 flex flex-wrap items-center justify-between gap-3 rounded-xl border border-emerald-500/35 bg-emerald-500/10 p-4">
                    <div>
                        <p className="text-[10px] font-bold uppercase tracking-wider text-emerald-600 dark:text-emerald-400">
                            Status · KI-Text vorhanden
                        </p>
                        <p className="mt-0.5 text-sm font-semibold text-pe-text dark:text-white">
                            Für diesen Plan liegt ein generierter KI-Businessplan-Text vor.
                        </p>
                    </div>
                    {onGoToTextEngine && (
                        <button
                            type="button"
                            onClick={onGoToTextEngine}
                            className="shrink-0 rounded-lg border border-emerald-500/40 bg-emerald-500/15 px-3.5 py-2 text-xs font-bold uppercase tracking-wider text-emerald-700 dark:text-emerald-300 hover:bg-emerald-500/25"
                        >
                            In Schritt 4 ansehen / PDF →
                        </button>
                    )}
                </div>
            )}

            {/* Speichern */}
            <div className="mb-6 rounded-xl border border-pe-border dark:border-[#2a2a2a] bg-pe-elevated dark:bg-[#141414] p-4">
                <p className="mb-3 text-sm font-semibold uppercase tracking-wider text-pe-muted dark:text-slate-400">
                    Arbeitsstand speichern
                </p>
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                    <div>
                        <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-pe-muted dark:text-slate-400">
                            Titel
                        </label>
                        <input
                            type="text"
                            value={titel}
                            onChange={(e) => setTitel(e.target.value)}
                            placeholder="z. B. Bankgespräch Q3 / Übernahme Stand"
                            className={INPUT_CLASS}
                        />
                    </div>
                    <div>
                        <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-pe-muted dark:text-slate-400">
                            Notiz (optional)
                        </label>
                        <input
                            type="text"
                            value={note}
                            onChange={(e) => setNote(e.target.value)}
                            placeholder="z. B. vor Kreditantrag"
                            className={INPUT_CLASS}
                        />
                    </div>
                </div>
                <div className="mt-3 flex flex-wrap items-center gap-2">
                    <button
                        type="button"
                        onClick={handleSave}
                        className="rounded-lg border border-[#E6C299]/40 bg-[#E6C299]/15 px-4 py-2.5 text-sm font-bold uppercase tracking-wider text-pe-accent-text dark:text-[#E6C299] hover:bg-[#E6C299]/25"
                    >
                        Als neuen Stand speichern
                    </button>
                    <button
                        type="button"
                        onClick={handleUpdateSelected}
                        disabled={!selected}
                        className="rounded-lg border border-pe-border-strong dark:border-[#333] bg-pe-elevated dark:bg-[#1a1a1a] px-4 py-2.5 text-sm font-bold uppercase tracking-wider text-pe-muted dark:text-slate-300 hover:border-[#E6C299]/40 disabled:cursor-not-allowed disabled:opacity-40"
                    >
                        Ausgewählten Eintrag aktualisieren
                    </button>
                    <p className="w-full text-xs text-slate-600 sm:w-auto">
                        Live: Kredit {formatCurrency(liveModel.kpis.finanzbedarf.kreditbetrag, 0)} ·
                        Rate {formatCurrency(liveModel.kpis.finanzbedarf.rateMonat, 0)} · Planumsatz{' '}
                        {formatCurrency(liveModel.kpis.planumsatzNetto, 0)}
                    </p>
                </div>
                {statusMsg && <p className="mt-3 text-sm text-pe-accent-text dark:text-[#E6C299]/90">{statusMsg}</p>}
            </div>

            {/* Liste */}
            <div className="mb-6">
                <p className="mb-3 text-sm font-semibold uppercase tracking-wider text-pe-muted dark:text-slate-400">
                    Archiv ({archiv.length})
                </p>
                {archiv.length === 0 ? (
                    <p className="rounded-lg border border-dashed border-pe-border-strong dark:border-[#333] bg-pe-elevated dark:bg-[#141414] px-4 py-6 text-center text-sm text-stone-500 dark:text-slate-500">
                        Noch keine Stände gespeichert.
                    </p>
                ) : (
                    <ul className="space-y-2">
                        {archiv.map((entry) => {
                            const active = entry.id === effectiveSelectedId;
                            return (
                                <li
                                    key={entry.id}
                                    className={`rounded-xl border px-4 py-3 ${
                                        active
                                            ? 'border-[#E6C299]/40 bg-[#E6C299]/10'
                                            : 'border-pe-border dark:border-[#2a2a2a] bg-pe-elevated dark:bg-[#141414]'
                                    }`}
                                >
                                    <div className="flex flex-wrap items-start justify-between gap-3">
                                        <button
                                            type="button"
                                            onClick={() => setSelectedId(entry.id)}
                                            className="min-w-0 flex-1 text-left"
                                        >
                                            <p className="text-sm font-semibold text-pe-text dark:text-white">
                                                {entry.titel}
                                            </p>
                                            <p className="mt-0.5 text-xs text-stone-500 dark:text-slate-500">
                                                {new Date(entry.timestamp).toLocaleString('de-DE')} ·{' '}
                                                {entry.kennzahlen.foundingType === 'TAKEOVER'
                                                    ? 'Übernahme'
                                                    : 'Neugründung'}{' '}
                                                · Kredit{' '}
                                                {formatCurrency(entry.kennzahlen.kreditbetrag, 0)}
                                            </p>
                                            {entry.note && (
                                                <p className="mt-1 text-xs text-slate-600">
                                                    {entry.note}
                                                </p>
                                            )}
                                        </button>
                                        <div className="flex flex-wrap gap-2">
                                            <button
                                                type="button"
                                                onClick={() => handleOpenForEdit(entry)}
                                                className="rounded-lg border border-[#E6C299]/40 bg-[#E6C299]/15 px-3 py-1.5 text-[10px] font-bold uppercase tracking-wider text-pe-accent-text dark:text-[#E6C299] hover:bg-[#E6C299]/25"
                                            >
                                                Öffnen &amp; bearbeiten
                                            </button>
                                            <button
                                                type="button"
                                                onClick={() => setSelectedId(entry.id)}
                                                className="rounded-lg border border-pe-border-strong dark:border-[#333] px-3 py-1.5 text-[10px] font-bold uppercase tracking-wider text-pe-muted dark:text-slate-400 hover:border-pe-border-strong dark:hover:border-[#444] hover:text-pe-text dark:hover:text-white"
                                            >
                                                Nur vergleichen
                                            </button>
                                            <button
                                                type="button"
                                                onClick={() => handleDelete(entry.id)}
                                                className="rounded-lg border border-red-500/30 px-3 py-1.5 text-[10px] font-bold uppercase tracking-wider text-red-400/90 hover:bg-red-950/30"
                                            >
                                                Löschen
                                            </button>
                                        </div>
                                    </div>
                                </li>
                            );
                        })}
                    </ul>
                )}
            </div>

            {/* Vergleiche */}
            {selected && (
                <div className="space-y-4">
                    <div className="rounded-xl border border-pe-border dark:border-[#2a2a2a] bg-pe-elevated dark:bg-[#141414] p-4">
                        <p className="mb-1 text-sm font-semibold uppercase tracking-wider text-pe-muted dark:text-slate-400">
                            Eingefrorener Plan (Snapshot)
                        </p>
                        <p className="mb-4 text-xs text-stone-500 dark:text-slate-500">
                            Was du damals für Bank / Planung festgehalten hast — unverändert, bis
                            du den Eintrag aktualisierst. Kein Plan-gegen-Plan-Δ mehr (das war
                            meist 0 und verwirrend).
                        </p>
                        <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-5">
                            {(
                                [
                                    ['Planumsatz netto', selected.kennzahlen.planumsatzNetto],
                                    ['Umsatzziel (Totpunkt)', selected.kennzahlen.totpunktMonatBrutto],
                                    ['Unternehmerlohn', selected.kennzahlen.unternehmerlohn],
                                    ['Kreditbetrag', selected.kennzahlen.kreditbetrag],
                                    ['Monatsrate', selected.kennzahlen.rateMonat],
                                ] as const
                            ).map(([label, val]) => (
                                <div
                                    key={label}
                                    className="rounded-lg border border-pe-border dark:border-[#222] bg-pe-card dark:bg-[#0f0f0f] px-3 py-2.5"
                                >
                                    <p className="text-[10px] uppercase tracking-wider text-stone-500 dark:text-slate-500">
                                        {label}
                                    </p>
                                    <p className="mt-1 text-sm font-bold tabular-nums text-pe-text dark:text-white">
                                        {formatCurrency(val, 0)}
                                    </p>
                                </div>
                            ))}
                        </div>
                    </div>

                    <div className="rounded-xl border border-pe-border dark:border-[#2a2a2a] bg-pe-elevated dark:bg-[#141414] p-4">
                        <p className="mb-1 text-sm font-semibold uppercase tracking-wider text-pe-muted dark:text-slate-400">
                            Der eigentliche Check: Plan ↔ echte Umsätze
                        </p>
                        <p className="mb-4 text-xs text-stone-500 dark:text-slate-500">
                            Hier ändert sich etwas: sobald in der Gesamtleistung Monate mit Umsatz
                            stehen, siehst du IST gegen den eingefrorenen Plan — Mid-Year-Check,
                            ob die Prognose hält.
                        </p>
                        {istMonate.length === 0 ? (
                            <p className="rounded-lg border border-dashed border-pe-border-strong dark:border-[#333] px-4 py-5 text-center text-sm text-stone-500 dark:text-slate-500">
                                Noch keine Umsätze in der Gesamtleistung eingetragen. Sobald Monate
                                gepflegt sind, erscheinen sie hier automatisch.
                            </p>
                        ) : (
                            <>
                                <div className="mb-4 grid grid-cols-1 gap-2 sm:grid-cols-3">
                                    <div className="rounded-lg border border-pe-border dark:border-[#222] bg-pe-card dark:bg-[#0f0f0f] px-3 py-2.5">
                                        <p className="text-[10px] uppercase tracking-wider text-stone-500 dark:text-slate-500">Ø IST netto ({istMonate.length} Mon.)</p>
                                        <p className="mt-1 text-sm font-bold tabular-nums text-pe-text dark:text-white">{formatCurrency(istDurchschnittNetto, 0)}</p>
                                    </div>
                                    <div className="rounded-lg border border-pe-border dark:border-[#222] bg-pe-card dark:bg-[#0f0f0f] px-3 py-2.5">
                                        <p className="text-[10px] uppercase tracking-wider text-stone-500 dark:text-slate-500">Archiv-Planumsatz netto</p>
                                        <p className="mt-1 text-sm font-bold tabular-nums text-pe-muted dark:text-slate-300">{formatCurrency(selected.kennzahlen.planumsatzNetto, 0)}</p>
                                        <p className={`mt-0.5 text-[10px] tabular-nums ${istDurchschnittNetto - selected.kennzahlen.planumsatzNetto >= 0 ? 'text-emerald-800 dark:text-emerald-400' : 'text-rose-400'}`}>
                                            Δ {istDurchschnittNetto - selected.kennzahlen.planumsatzNetto > 0 ? '+' : ''}{formatCurrency(istDurchschnittNetto - selected.kennzahlen.planumsatzNetto, 0)}
                                        </p>
                                    </div>
                                    <div className="rounded-lg border border-pe-border dark:border-[#222] bg-pe-card dark:bg-[#0f0f0f] px-3 py-2.5">
                                        <p className="text-[10px] uppercase tracking-wider text-stone-500 dark:text-slate-500">Ø Archiv-Prognose brutto (M1–6)</p>
                                        <p className="mt-1 text-sm font-bold tabular-nums text-pe-muted dark:text-slate-300">{formatCurrency(archivPrognoseSchnittBrutto, 0)}</p>
                                        <p className={`mt-0.5 text-[10px] tabular-nums ${istDurchschnittBrutto - archivPrognoseSchnittBrutto >= 0 ? 'text-emerald-800 dark:text-emerald-400' : 'text-rose-400'}`}>
                                            Δ IST {istDurchschnittBrutto - archivPrognoseSchnittBrutto > 0 ? '+' : ''}{formatCurrency(istDurchschnittBrutto - archivPrognoseSchnittBrutto, 0)}
                                        </p>
                                    </div>
                                </div>
                                <div className="overflow-x-auto">
                                    <table className="w-full min-w-[640px] border-collapse text-left text-sm">
                                        <thead>
                                            <tr className="border-b border-pe-border dark:border-[#2a2a2a] text-[10px] uppercase tracking-wider text-stone-500 dark:text-slate-500">
                                                <th className="px-2 py-2 font-semibold">Monat</th>
                                                <th className="px-2 py-2 font-semibold text-right">IST brutto</th>
                                                <th className="px-2 py-2 font-semibold text-right">Archiv-Ziel</th>
                                                <th className="px-2 py-2 font-semibold text-right">Δ zu Ziel</th>
                                                <th className="px-2 py-2 font-semibold text-right">Δ zu Planumsatz*</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {istMonate.map((m: { index: number; label: string; brutto: number }) => {
                                                const ziel = selected.kennzahlen.totpunktMonatBrutto;
                                                const mwstRateRow =
                                                    liveModel.mwstSatz !== undefined && liveModel.mwstSatz !== null && Number.isFinite(Number(liveModel.mwstSatz))
                                                        ? Number(liveModel.mwstSatz)
                                                        : 19;
                                                const planBrutto = selected.kennzahlen.planumsatzNetto * (1 + mwstRateRow / 100);
                                                const dZiel = m.brutto - ziel;
                                                const dPlan = m.brutto - planBrutto;
                                                return (
                                                    <tr key={m.index} className="border-b border-pe-border dark:border-[#222]">
                                                        <td className="px-2 py-2 font-semibold text-pe-muted dark:text-slate-300">{m.label}</td>
                                                        <td className="px-2 py-2 text-right tabular-nums text-pe-accent-text dark:text-[#E6C299]">{formatCurrency(m.brutto, 0)}</td>
                                                        <td className="px-2 py-2 text-right tabular-nums text-pe-muted dark:text-slate-400">{formatCurrency(ziel, 0)}</td>
                                                        <td className={`px-2 py-2 text-right tabular-nums font-semibold ${dZiel >= 0 ? 'text-emerald-800 dark:text-emerald-400' : 'text-rose-400'}`}>{dZiel > 0 ? '+' : ''}{formatCurrency(dZiel, 0)}</td>
                                                        <td className={`px-2 py-2 text-right tabular-nums ${dPlan >= 0 ? 'text-emerald-800 dark:text-emerald-400' : 'text-rose-400'}`}>{dPlan > 0 ? '+' : ''}{formatCurrency(dPlan, 0)}</td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                </div>
                                <p className="mt-2 text-[10px] text-slate-600">
                                    * Planumsatz = archivierter Planumsatz netto × MwSt. Quelle: Gesamtleistung (Bedien + Verkauf).
                                </p>
                            </>
                        )}
                    </div>

                    {selected.kennzahlen.monate.length > 0 && (
                        <div className="rounded-xl border border-pe-border dark:border-[#2a2a2a] bg-pe-elevated dark:bg-[#141414] p-4">
                            <p className="mb-2 text-[10px] font-semibold uppercase tracking-wider text-stone-500 dark:text-slate-500">
                                Archivierte Anlauf-Prognose (M1–M6)
                            </p>
                            <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-6">
                                {selected.kennzahlen.monate.map((m) => (
                                    <div key={m.month} className="rounded-lg border border-pe-border dark:border-[#222] bg-pe-card dark:bg-[#0f0f0f] px-2 py-2">
                                        <p className="text-[10px] uppercase text-stone-500 dark:text-slate-500">M{m.month}</p>
                                        <p className="mt-1 text-xs font-bold tabular-nums text-pe-accent-text dark:text-[#E6C299]">{formatCurrency(m.umsatzPrognose, 0)}</p>
                                        <p className="text-[10px] tabular-nums text-slate-600">Ziel {formatCurrency(m.umsatzZiel, 0)}</p>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            )}
        </section>
    );
};

export default PEStartArchivPanel;
